import { useMemo, useState } from 'react';
import { IconAlertTriangle, IconCopy, IconDownload, IconFileText, IconLock, IconPlus, IconSearch, IconStopwatch } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { HeaderTools, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, StateView, Skeleton, useToast, type ScreenState } from '@hana/ui';
import { NO_CONTRACT, QUICK, creditText, dupCounts, expiryText, expiryTone, quickCount, quickMatch, searchContracts, type QuickKey } from './contractListData';
import { ContractDock, useContractDock } from './ContractDock';
import type { Contract } from './types';
import { useT } from '@hana/i18n';
import './i18n';
import s from './contracts.module.css';

// Mint-Contract (desktop 1536) · tab Danh sách. Chỉ đọc; nút ghi báo toast. Tiền (hạn mức) ẩn: board money=hide.
// Tab Mẫu hợp đồng / Rà trùng và ngăn chi tiết chưa mở ở bản này.

const COLS = [
  ['code', 190], ['cust', 176], ['date', 120], ['exp', 150], ['debt', 170], ['lang', 110], ['appr', 110], ['sign', 134], ['sale', 80],
] as const;
const TOTAL = COLS.reduce((a, c) => a + c[1], 0);
const pct = (w: number) => `${((w / TOTAL) * 100).toFixed(3)}%`;

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

export function ContractList() {
  const toast = useToast();
  const t = useT();
  const dock = useContractDock();
  const { data, error, isLoading, isError, refetch } = useList('crmContract', { limit: 5000 });
  const forced = readState();
  const [qf, setQf] = useState<QuickKey>('all');
  const [q, setQ] = useState('');
  const all = useMemo<readonly Contract[]>(() => data?.items ?? [], [data]);
  const dup = useMemo(() => dupCounts(all), [all]);
  const listed = useMemo(() => searchContracts(all.filter((x) => quickMatch(qf, x, dup)), q), [all, qf, q, dup]);

  const noWrite = () => toast({ msg: t('crm.noWrite') });
  const later = () => toast({ msg: t('crm.notYet') });
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';
  const ready = state === 'ready';
  const csv = () => toast({ msg: t('crm.ct.csvDone', { n: listed.length }) });

  return (
    <Page kind="table" dock={dock.wide && ready ? <ContractDock rows={all} dock={dock} onFilter={setQf} /> : undefined}>
      <PageHeader
        title={t('crm.ct.title')}
        sub={t('crm.ct.sub')}
        tabs={
          <Segmented
            label={t('crm.ct.group')}
            value="list"
            onChange={(k) => (k === 'list' ? undefined : later())}
            items={[{ key: 'list', label: t('crm.ct.tabList'), n: ready ? all.length : undefined }, { key: 'tpl', label: t('crm.ct.tabTpl'), n: 5 }, { key: 'dup', label: t('crm.ct.tabDup'), n: ready ? Object.values(dup).filter((v) => v > 1).length : undefined }]}
          />
        }
        actions={
          <>
            <HeaderTools />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
              {t('crm.ct.create')}
            </Button>
          </>
        }
      />
      <div className={s.page}>
        {ready && (
          <div className={s.tb}>
            <label className={s.search}>
              <IconSearch size={16} aria-hidden="true" />
              <input aria-label={t('crm.ct.search')} placeholder={t('crm.ct.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <div role="group" aria-label={t('crm.ct.quick')} className={s.chips}>
              {QUICK.map((f) => {
                const n = quickCount(f.key, all);
                return (
                  <button key={f.key} type="button" className={s.chip} aria-pressed={qf === f.key} onClick={() => setQf(f.key)}>
                    {t(`crm.ct.q.${f.key}`)} <span className={`${s.n} ${(f.key === 'e30' || f.key === 'noc') && n ? s.hot : ''}`}>{n}</span>
                  </button>
                );
              })}
            </div>
            <div className={s.sum}>
              {t('crm.ct.countOf', { n: listed.length, total: all.length })}
            </div>
          </div>
        )}
        <StateView
          state={state}
          title={state === 'empty' ? t('crm.ct.emptyTitle') : state === 'error' ? t('crm.ct.errTitle') : undefined}
          desc={state === 'empty' ? t('crm.ct.emptyDesc') : undefined}
          icon={state === 'empty' ? <IconFileText size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('common.retry')}</Button> : state === 'empty' ? <Button onClick={noWrite}>{t('crm.ct.createPlus')}</Button> : undefined}
          skeleton={<Skeleton rows={8} />}
        >
          {qf === 'noc' ? (
            <section className={s.card} aria-label={t('crm.ct.nocLabel')}>
              <div className={s.nocHead}>{t('crm.ct.nocHead')}</div>
              {NO_CONTRACT.map((r) => (
                <div key={r.sn} className={s.nocRow}>
                  <div>
                    <b className={s.b}>{r.sn}</b>
                    <div className={s.sub}>MST {r.mst}</div>
                  </div>
                  <span>{t('crm.ct.nocJobs', { n: r.jobs, last: r.last })}</span>
                  <span className={s.tx2}>{t('crm.ct.sale', { v: r.sale })}</span>
                  <span className={s.r}>
                    <button type="button" className={s.mk} onClick={noWrite}>
                      <IconPlus size={16} aria-hidden="true" />
                      {t('crm.ct.create')}
                    </button>
                  </span>
                </div>
              ))}
            </section>
          ) : (
            <section className={s.card} aria-label={t('crm.ct.listLabel')}>
              <div className={s.lock}>
                <IconLock size={14} aria-hidden="true" />
                {t('crm.ct.noMoney')}
              </div>
              <table className={s.table} aria-label={t('crm.ct.table')} aria-rowcount={listed.length + 1}>
                <colgroup>
                  {COLS.map((c) => (
                    <col key={c[0]} style={{ width: pct(c[1]) }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {COLS.map((c) => (
                      <th key={c[0]} scope="col">{t(`crm.ct.col.${c[0]}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {listed.map((x, i) => {
                    const tone = expiryTone(x);
                    const txt = expiryText(x, dup);
                    return (
                      <tr key={x.code} aria-rowindex={i + 2} onClick={later}>
                        <td>
                          <div className={s.codeRow}>
                            <b className={s.code}>{x.code}</b>
                            <button type="button" className={s.copy} title={t('crm.ct.copyTitle')} aria-label={t('crm.ct.copyAria', { code: x.code })} onClick={(e) => { e.stopPropagation(); toast({ msg: t('crm.ct.copied', { code: x.code }) }); }}>
                              <IconCopy size={14} aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                        <td>
                          <b className={s.b}>{x.customer}</b>
                          <div className={s.sub} title={`MST ${x.mst}`}>{x.mst}</div>
                        </td>
                        <td className={s.tx2}>{x.signedOn}</td>
                        <td className={tone === 'err' ? s.err : tone === 'warn' ? s.warn : s.tx2}>
                          {x.expiresOn}
                          <div className={s.sub} title={txt}>
                            {x.daysLeft >= 0 && x.daysLeft <= 60 && <IconStopwatch size={12} stroke={1.8} aria-hidden="true" />}
                            {x.daysLeft >= 0 && x.daysLeft <= 60 ? t('crm.ct.days', { n: x.daysLeft }) : txt}
                          </div>
                        </td>
                        <td>{creditText(x)}</td>
                        <td className={s.tx2}>{x.lang === 'Bilingual' ? t('crm.ct.bilingual') : t('crm.ct.vi')}</td>
                        <td><span className={`${s.pill} ${x.approved ? s.ok : s.wn}`}>{x.approved ? t('crm.ct.approved') : t('crm.ct.pending')}</span></td>
                        <td>
                          <span className={`${s.pill} ${x.signed ? s.ok : s.wn}`}>{x.signed ? t('crm.ct.signed') : t('crm.ct.unsigned')}</span>
                          {x.missing.length > 0 && (
                            <div className={`${s.sub} ${s.err}`} title={t('crm.ct.missing', { v: x.missing.join(', ') })}>
                              <IconAlertTriangle size={12} stroke={1.8} aria-hidden="true" />
                              {t('crm.ct.missing', { v: x.missing.join(', ') })}
                            </div>
                          )}
                        </td>
                        <td>{x.sale}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {listed.length === 0 && <div className={s.empty}>{t('crm.ct.noMatch')}</div>}
              <div className={s.foot}>
                <span>{t('crm.ct.count', { n: listed.length })}</span>
                <button type="button" className={s.csv} onClick={csv}>
                  <IconDownload size={16} aria-hidden="true" />
                  {t('crm.ct.csv')}
                </button>
              </div>
            </section>
          )}
        </StateView>
      </div>
    </Page>
  );
}
